// ═══════════════════════════════════════════════════
// ErrorBoundary — Three-tier error catching
// Because even compliance tools need a safety net
// ═══════════════════════════════════════════════════

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { WarningOctagon, ArrowCounterClockwise, Bug } from '@phosphor-icons/react';

type ErrorLevel = 'app' | 'route' | 'widget';

interface Props {
  children: ReactNode;
  level?: ErrorLevel;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Fun error messages because bad days don't need boring error screens
const ERROR_MESSAGES: Record<ErrorLevel, { title: string; messages: string[] }> = {
  app: {
    title: 'Well, this is awkward.',
    messages: [
      'Something went seriously sideways. Our team has been notified.',
      'The whole app tripped over itself. We\'re on it.',
      'Houston, we have a problem. But we\'re fixing it.',
    ],
  },
  route: {
    title: 'This page had a moment.',
    messages: [
      'This view couldn\'t load, but the rest of the app is fine. Try refreshing.',
      'Something broke on this page. Your other data is safe and sound.',
      'This page needs a coffee break. Give it another shot.',
    ],
  },
  widget: {
    title: 'Couldn\'t load this section',
    messages: [
      'This widget is taking a personal day.',
      'Minor hiccup here. Everything else is running fine.',
      'This section is temporarily out of compliance. Ironic, we know.',
    ],
  },
};

function getRandomMessage(level: ErrorLevel): string {
  const { messages } = ERROR_MESSAGES[level];
  return messages[Math.floor(Math.random() * messages.length)] ?? messages[0]!;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Report to Sentry (when initialized)
    // Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    console.error(
      `[ErrorBoundary:${this.props.level ?? 'app'}]`,
      error,
      errorInfo.componentStack,
    );
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const level = this.props.level ?? 'app';
    const { title } = ERROR_MESSAGES[level];
    const message = getRandomMessage(level);

    // Widget-level: compact inline error
    if (level === 'widget') {
      return (
        <div style={widgetErrorStyles.container}>
          <WarningOctagon size={20} weight="duotone" style={{ color: '#FFB800' }} />
          <div>
            <p style={widgetErrorStyles.title}>{title}</p>
            <p style={widgetErrorStyles.message}>{message}</p>
          </div>
          <button onClick={this.handleReset} style={widgetErrorStyles.retryBtn}>
            <ArrowCounterClockwise size={14} />
            Retry
          </button>
        </div>
      );
    }

    // Route-level and App-level: centered card
    return (
      <div style={fullErrorStyles.container}>
        <div style={fullErrorStyles.card}>
          <div style={fullErrorStyles.iconWrap}>
            <Bug size={32} weight="duotone" style={{ color: '#FFB800' }} />
          </div>
          <h2 style={fullErrorStyles.title}>{title}</h2>
          <p style={fullErrorStyles.message}>{message}</p>

          <div style={fullErrorStyles.actions}>
            <button onClick={this.handleReset} style={fullErrorStyles.primaryBtn}>
              <ArrowCounterClockwise size={16} />
              Try Again
            </button>
            {level === 'app' && (
              <button onClick={this.handleReload} style={fullErrorStyles.secondaryBtn}>
                Reload Page
              </button>
            )}
          </div>

          {/* Error details for devs */}
          {import.meta.env.DEV && this.state.error && (
            <details style={fullErrorStyles.details}>
              <summary style={fullErrorStyles.detailsSummary}>
                Error details (dev only)
              </summary>
              <pre style={fullErrorStyles.detailsPre}>
                {this.state.error.message}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

// ── Inline Styles (no CSS module dependency for error states) ──

const widgetErrorStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '12px',
    background: 'rgba(255, 184, 0, 0.06)',
    border: '1px solid rgba(255, 184, 0, 0.15)',
  } as const,
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '13px',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '2px',
  } as const,
  message: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.5,
  } as const,
  retryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.06)',
    color: 'var(--text-secondary)',
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: 'auto',
    flexShrink: 0,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'var(--font-body)',
  } as const,
};

const fullErrorStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '24px',
  } as const,
  card: {
    maxWidth: '440px',
    width: '100%',
    background: 'rgba(255, 255, 255, 0.024)',
    border: '1px solid rgba(255, 255, 255, 0.058)',
    borderRadius: '20px',
    padding: '40px 32px',
    textAlign: 'center' as const,
  },
  iconWrap: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'rgba(255, 184, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  } as const,
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '20px',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    marginBottom: '8px',
  } as const,
  message: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
    marginBottom: '28px',
  } as const,
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
  } as const,
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #7B2FFF, #00A8FF)',
    color: 'white',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'var(--font-body)',
    transition: 'transform 0.2s',
  } as const,
  secondaryBtn: {
    padding: '10px 20px',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.06)',
    color: 'var(--text-primary)',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: 'var(--font-body)',
  } as const,
  details: {
    marginTop: '24px',
    textAlign: 'left' as const,
  },
  detailsSummary: {
    fontFamily: 'var(--font-mono)',
    fontSize: '10px',
    letterSpacing: '0.1em',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    textTransform: 'uppercase' as const,
  },
  detailsPre: {
    marginTop: '8px',
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(0, 0, 0, 0.3)',
    fontFamily: 'var(--font-mono)',
    fontSize: '10px',
    color: 'var(--text-muted)',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
};

